import React, { useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Reorder } from 'framer-motion';
import {
  Activity, Pill, Shield, Users, Calendar, FileText,
  Settings2, GripVertical, RotateCcw
} from 'lucide-react';
import StatusChip from '../shared/StatusChip';

// ---- Abbreviation helpers ----
const ABBREVIATIONS = {
  'type 2 diabetes mellitus': 'DM2', 'type 1 diabetes mellitus': 'DM1',
  'essential hypertension': 'HTN', 'hypertension': 'HTN',
  'coronary artery disease': 'CAD', 'congestive heart failure': 'CHF',
  'chronic obstructive pulmonary disease': 'COPD', 'copd': 'COPD',
  'atrial fibrillation': 'AFib', 'chronic kidney disease': 'CKD',
  'gastroesophageal reflux disease': 'GERD', 'generalized anxiety disorder': 'GAD',
  'major depressive disorder': 'MDD', 'right knee pain': 'R Knee Pain',
  'left knee pain': 'L Knee Pain', 'low back pain': 'LBP',
  'urinary tract infection': 'UTI', 'deep vein thrombosis': 'DVT',
  'pulmonary embolism': 'PE', 'benign prostatic hyperplasia': 'BPH',
  'obstructive sleep apnea': 'OSA', 'hypothyroidism': 'Hypothyroid',
  'hyperlipidemia': 'HLD',
};

function abbreviate(name) {
  const lower = name.toLowerCase();
  if (ABBREVIATIONS[lower]) return ABBREVIATIONS[lower];
  if (name.length <= 12) return name;
  return name.split(' ').map(w => w.length > 5 ? w.slice(0, 4) + '.' : w).join(' ');
}

// ---- Default widget config ----
const DEFAULT_WIDGETS = [
  { id: 'problems', label: 'Active Problems', icon: 'Activity', colSpan: 1, visible: true },
  { id: 'meds', label: 'Medications', icon: 'Pill', colSpan: 1, visible: true },
  { id: 'preventive', label: 'Preventive Care', icon: 'Shield', colSpan: 1, visible: true },
  { id: 'care_team', label: 'Care Team', icon: 'Users', colSpan: 1, visible: true },
  { id: 'recent_visits', label: 'Recent Visits', icon: 'Calendar', colSpan: 2, visible: true },
];

const ICON_MAP = { Activity, Pill, Shield, Users, Calendar };

const STORAGE_KEY = 'hfc_overview_layout';

function loadLayout() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return DEFAULT_WIDGETS;
    const parsed = JSON.parse(saved);
    // Merge with defaults to handle new widgets added later
    const savedIds = new Set(parsed.map(w => w.id));
    const merged = [
      ...parsed,
      ...DEFAULT_WIDGETS.filter(d => !savedIds.has(d.id)),
    ];
    return merged;
  } catch {
    return DEFAULT_WIDGETS;
  }
}

function saveLayout(widgets) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets));
}

// ---- Individual Widget Renderers ----

function ProblemsWidget({ patient, onNavigate }) {
  const activeProblems = (patient.problem_list || []).filter(p => p.status === 'active');
  const chronic = activeProblems.filter(p => p.classification === 'chronic');
  const acute = activeProblems.filter(p => p.classification === 'acute');

  return (
    <Card
      className="cursor-pointer hover:shadow-md hover:border-slate-300 transition-all"
      onClick={() => onNavigate('problems')}
      data-testid="overview-problems-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-500" /> Active Problems
          <Badge variant="outline" className="text-[10px] ml-auto">{activeProblems.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1.5">
          {chronic.map((p, i) => (
            <span key={`c-${i}`} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
              {abbreviate(p.condition_name)}
            </span>
          ))}
          {acute.map((p, i) => (
            <span key={`a-${i}`} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
              {abbreviate(p.condition_name)}
            </span>
          ))}
          {activeProblems.length === 0 && <p className="text-xs text-muted-foreground">No active problems</p>}
        </div>
        {(chronic.length > 0 || acute.length > 0) && (
          <div className="flex items-center gap-3 mt-2 pt-2 border-t">
            {chronic.length > 0 && (
              <span className="text-[10px] text-blue-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />{chronic.length} chronic
              </span>
            )}
            {acute.length > 0 && (
              <span className="text-[10px] text-amber-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />{acute.length} acute
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MedsWidget({ patient, onNavigate }) {
  const activeMeds = (patient.medications || []).filter(m => m.status === 'active');
  return (
    <Card
      className="cursor-pointer hover:shadow-md hover:border-slate-300 transition-all"
      onClick={() => onNavigate('meds')}
      data-testid="overview-meds-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Pill className="w-4 h-4 text-emerald-500" /> Medications
          <Badge variant="outline" className="text-[10px] ml-auto">{activeMeds.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {activeMeds.slice(0, 5).map((m, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-sm">{m.name}</span>
            <span className="text-xs text-muted-foreground">{m.dose}</span>
          </div>
        ))}
        {activeMeds.length > 5 && <p className="text-[10px] text-muted-foreground">+{activeMeds.length - 5} more</p>}
        {activeMeds.length === 0 && <p className="text-xs text-muted-foreground">No active medications</p>}
      </CardContent>
    </Card>
  );
}

function PreventiveWidget({ patient, onNavigate }) {
  const preventive = patient.preventive_care || [];
  return (
    <Card
      className="cursor-pointer hover:shadow-md hover:border-slate-300 transition-all"
      onClick={() => onNavigate('preventive')}
      data-testid="overview-preventive-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-500" /> Preventive Care
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {preventive.map((item, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-sm">{item.item_name}</span>
            <Badge variant="outline" className={`text-[10px] ${
              item.status === 'overdue' ? 'bg-red-50 text-red-600 border-red-200'
              : item.status === 'due_soon' ? 'bg-amber-50 text-amber-600 border-amber-200'
              : 'bg-emerald-50 text-emerald-600 border-emerald-200'
            }`}>{item.status?.replace('_', ' ')}</Badge>
          </div>
        ))}
        {preventive.length === 0 && <p className="text-xs text-muted-foreground">No items</p>}
      </CardContent>
    </Card>
  );
}

function CareTeamWidget({ patient, onNavigate }) {
  return (
    <Card
      className="cursor-pointer hover:shadow-md hover:border-slate-300 transition-all"
      onClick={() => onNavigate('care_team')}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="w-4 h-4 text-sky-500" /> Care Team
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {(patient.care_team || []).map((ct, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-sky-50 flex items-center justify-center text-[9px] font-medium text-sky-600">
              {ct.provider_name?.charAt(0)}
            </div>
            <span className="text-sm">{ct.provider_name}</span>
            <span className="text-xs text-muted-foreground">{ct.role}</span>
          </div>
        ))}
        {(!patient.care_team || patient.care_team.length === 0) && (
          <p className="text-xs text-muted-foreground">No care team</p>
        )}
      </CardContent>
    </Card>
  );
}

function RecentVisitsWidget({ encounters, onNavigate }) {
  return (
    <Card
      className="col-span-2 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all"
      onClick={() => onNavigate('notes')}
      data-testid="overview-visits-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Calendar className="w-4 h-4 text-violet-500" /> Recent Visits
          <Badge variant="outline" className="text-[10px] ml-auto">{encounters.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {encounters.slice(0, 4).map((enc, i) => (
          <div key={enc.id || i} className="flex items-start gap-3 py-2 border-b last:border-0">
            <div className="w-7 h-7 rounded-full bg-violet-50 flex items-center justify-center shrink-0">
              <FileText className="w-3.5 h-3.5 text-violet-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium tabular-nums">{enc.date?.split('T')[0]}</span>
                <StatusChip status={enc.status} size="xs" />
                {enc.provider_name && <span className="text-xs text-muted-foreground">{enc.provider_name}</span>}
              </div>
              {enc.encounter_outputs?.visit_summary?.summary_short && (
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {enc.encounter_outputs.visit_summary.summary_short}
                </p>
              )}
            </div>
          </div>
        ))}
        {encounters.length === 0 && (
          <p className="text-xs text-muted-foreground py-4 text-center">No visit history</p>
        )}
      </CardContent>
    </Card>
  );
}

const WIDGET_RENDERERS = {
  problems: ProblemsWidget,
  meds: MedsWidget,
  preventive: PreventiveWidget,
  care_team: CareTeamWidget,
  recent_visits: RecentVisitsWidget,
};

// ---- Customization Dialog ----
function CustomizeDialog({ open, onOpenChange, widgets, onSave }) {
  const [items, setItems] = useState(widgets);

  const handleReorder = useCallback((newOrder) => {
    setItems(newOrder);
  }, []);

  const toggleVisible = useCallback((id) => {
    setItems(prev => prev.map(w => w.id === id ? { ...w, visible: !w.visible } : w));
  }, []);

  const reset = useCallback(() => {
    setItems(DEFAULT_WIDGETS);
  }, []);

  const handleSave = () => {
    onSave(items);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="overview-customize-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Settings2 className="w-4 h-4 text-primary" />
            Customize Overview
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground -mt-1">
          Drag to reorder. Toggle to show or hide widgets.
        </p>

        <ScrollArea className="max-h-[320px]">
          <Reorder.Group
            axis="y"
            values={items}
            onReorder={handleReorder}
            className="space-y-1.5"
          >
            {items.map(widget => {
              const Icon = ICON_MAP[widget.icon] || Activity;
              return (
                <Reorder.Item
                  key={widget.id}
                  value={widget}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-white hover:shadow-sm cursor-grab active:cursor-grabbing active:shadow-md transition-shadow"
                  data-testid={`customize-widget-${widget.id}`}
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground/40 shrink-0" />
                  <Icon className="w-4 h-4 text-primary/60 shrink-0" />
                  <span className="text-sm flex-1">{widget.label}</span>
                  <Switch
                    checked={widget.visible}
                    onCheckedChange={() => toggleVisible(widget.id)}
                    className="scale-75"
                    data-testid={`customize-toggle-${widget.id}`}
                  />
                </Reorder.Item>
              );
            })}
          </Reorder.Group>
        </ScrollArea>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <Button variant="ghost" size="sm" className="text-xs gap-1.5" onClick={reset} data-testid="customize-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset
          </Button>
          <Button size="sm" className="text-xs" onClick={handleSave} data-testid="customize-save-btn">
            Save Layout
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Main Overview Component ----
export default function OverviewContent({ patient, encounters, onNavigate }) {
  const [widgets, setWidgets] = useState(loadLayout);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const handleSaveLayout = useCallback((newWidgets) => {
    setWidgets(newWidgets);
    saveLayout(newWidgets);
  }, []);

  const visibleWidgets = useMemo(() => widgets.filter(w => w.visible), [widgets]);

  return (
    <div data-testid="overview-content">
      {/* Customize button */}
      <div className="flex justify-end mb-3">
        <Button
          variant="ghost"
          size="sm"
          className="text-xs gap-1.5 text-muted-foreground hover:text-foreground"
          onClick={() => setCustomizeOpen(true)}
          data-testid="overview-customize-btn"
        >
          <Settings2 className="w-3.5 h-3.5" />
          Customize
        </Button>
      </div>

      {/* Widget Grid */}
      <div className="grid grid-cols-2 gap-4">
        {visibleWidgets.map(widget => {
          const Renderer = WIDGET_RENDERERS[widget.id];
          if (!Renderer) return null;
          return (
            <div key={widget.id} className={widget.colSpan === 2 ? 'col-span-2' : ''}>
              <Renderer patient={patient} encounters={encounters} onNavigate={onNavigate} />
            </div>
          );
        })}
      </div>

      {visibleWidgets.length === 0 && (
        <div className="text-center py-16">
          <Settings2 className="w-8 h-8 mx-auto mb-2 text-muted-foreground/20" />
          <p className="text-sm text-muted-foreground">No widgets visible</p>
          <Button variant="link" size="sm" className="text-xs mt-1" onClick={() => setCustomizeOpen(true)}>
            Customize Overview
          </Button>
        </div>
      )}

      <CustomizeDialog
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
        widgets={widgets}
        onSave={handleSaveLayout}
      />
    </div>
  );
}
